import { useCallback, useEffect, useRef, useState } from 'react';
import { useShadowGlobalProps } from '@elfsight/widget-shadow';

export function useOutsideClick(callback: (event: PointerEvent) => void) {
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const callbackRef = useRef(callback);
  const hasShadowProps = !!useShadowGlobalProps();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const targetElementCallbackRef = useCallback((element: Element | null) => {
    setTargetElement(element);
  }, []);

  useEffect(() => {
    if (!targetElement) {
      return;
    }

    const targetRoot = targetElement.getRootNode();
    const isInShadowRoot = !!hasShadowProps && targetRoot instanceof ShadowRoot;

    let clickedInside = false;

    const handlePointerDownInside = () => {
      clickedInside = true;
    };

    const handlePointerUp = (event: Event) => {
      const eventTarget = event.target;

      if (
        !(eventTarget instanceof Element) ||
        !(event instanceof PointerEvent)
      ) {
        return;
      }

      if (
        clickedInside ||
        targetElement?.contains(eventTarget) ||
        eventTarget.closest('select')
      ) {
        clickedInside = false;

        return;
      }

      callbackRef.current(event);
      clickedInside = false;
    };

    // Чтобы отловить клик «снаружи» из закрытого(!) shadow DOM,
    // нужно слушать события клика и внутри shadow DOM, и за его пределами.
    // composedPath здесь не помогает, так как shadow DOM закрыт,
    // а event.target в window-слушателе возвращает host-элемент shadowRoot
    const handleWindowPointerUp = (event: Event) => {
      if (isInShadowRoot) {
        let host = targetRoot.host;
        let hostRoot = host.getRootNode();

        // на случай вложенных shadow DOM
        while (event.target !== host && hostRoot instanceof ShadowRoot) {
          host = hostRoot.host;
          hostRoot = host.getRootNode();
        }

        // дошли до host-элемента shadowRoot — клик был внутри shadow DOM
        if (event.target === host) {
          return;
        }
      }

      handlePointerUp(event);
    };

    targetElement.addEventListener('pointerdown', handlePointerDownInside);

    if (isInShadowRoot) {
      window.addEventListener('pointerup', handleWindowPointerUp, true);
      targetRoot.addEventListener('pointerup', handlePointerUp, true);
    } else {
      window.addEventListener('pointerup', handlePointerUp, true);
    }

    return () => {
      targetElement.removeEventListener('pointerdown', handlePointerDownInside);

      if (isInShadowRoot) {
        window.removeEventListener('pointerup', handleWindowPointerUp, true);
        targetRoot.removeEventListener('pointerup', handlePointerUp, true);
      } else {
        window.removeEventListener('pointerup', handlePointerUp, true);
      }
    };
  }, [targetElement, hasShadowProps]);

  return targetElementCallbackRef;
}
